// src/components/auth/AuthForgotPassword.tsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
    Mail,
    ArrowRight,
    Loader2,
    ArrowLeft,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

// Schema moved inside component to use substitution
const forgotPasswordBaseSchema = z.object({
    email: z.string().email(),
});

type ForgotPasswordSchema = z.infer<typeof forgotPasswordBaseSchema>;

interface AuthForgotPasswordProps {
    loading: boolean;
    onSendReset: (email: string) => Promise<void>;
    serverError?: string | null;
    isSent: boolean;
    onSuccess: () => void;
}

const AuthForgotPassword: React.FC<AuthForgotPasswordProps> = ({
    loading,
    onSendReset,
    serverError,
    isSent,
    onSuccess,
}) => {
    const [sentEmail, setSentEmail] = useState("");

    const { t } = useTranslation();

    const forgotPasswordSchema = z.object({
        email: z.string().email(t('auth.validation.email_invalid')),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ForgotPasswordSchema>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: {
            email: "",
        },
    });

    const onSubmit = async (data: ForgotPasswordSchema) => {
        try {
            await onSendReset(data.email);
            setSentEmail(data.email);
            onSuccess();
        } catch {
            // Error managed by parent
        }
    };

    if (isSent) {
        return (
            <div className="text-center space-y-6 animate-in fade-in zoom-in duration-300">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 size={40} className="text-green-500" />
                </div>
                <div>
                    <h3 className="text-2xl font-bold text-gray-900">{t('auth.forgot.email_sent_title')}</h3>
                    <p className="text-gray-500 mt-2 text-sm leading-relaxed max-w-xs mx-auto">
                        {t('auth.forgot.email_sent_desc', { email: sentEmail })}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {serverError && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2 text-red-600 text-sm font-medium"
                >
                    <AlertCircle size={16} />
                    {serverError}
                </motion.div>
            )}

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                    {t('auth.forgot.recovery_email')}
                </label>
                <div className="relative group">
                    <Mail
                        className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${errors.email ? "text-red-400" : "text-gray-400 group-focus-within:text-clip-500"
                            }`}
                        size={18}
                    />
                    <input
                        type="email"
                        {...register("email")}
                        className={`w-full pl-11 pr-4 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
                             ${errors.email
                                ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                : "border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200"
                            }
                        `}
                        placeholder={t('auth.placeholders.email')}
                    />
                </div>
                {errors.email && (
                    <p className="text-red-500 text-xs font-medium ml-1 flex items-center gap-1">
                        {errors.email.message}
                    </p>
                )}
            </div>

            <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 text-white font-bold rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 mt-2
          ${loading
                        ? "bg-gray-300 cursor-not-allowed shadow-none"
                        : "bg-gray-900 hover:bg-black shadow-gray-200/50 hover:shadow-gray-300/50"
                    }
        `}
            >
                {loading ? (
                    <Loader2 size={20} className="animate-spin" />
                ) : (
                    <>
                        {t('auth.forgot.submit_btn')}
                        <ArrowRight size={18} />
                    </>
                )}
            </button>
        </form>
    );
};

export default AuthForgotPassword;